import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Layout from './components/Layout';
import Overview from './pages/Overview';
import Dashboard from './pages/Dashboard';
import TeamChat from './pages/TeamChat';
import AIAssistant from './pages/AIAssistant';
import Documents from './pages/Documents';
import Settings from './pages/Settings';
import Logger from './pages/Logger';
//import Inventory from './pages/Inventory';
import Profile from './pages/Profile';
import LabStore from './pages/LabStore';
import PrinterUsage from './pages/PrinterUsage';
import LabInventory from './pages/LabInventory';
import Login from './pages/Login';
import PersonalNotes from './pages/PersonalNotes';
import ExperimentProjects from './pages/ExperimentProjects';
import ExperimentTracking from './pages/ExperimentTracking';
import ProjectDetail from './pages/ProjectDetail';
import Notifications from './pages/Notifications';
import UserManagement from './pages/UserManagement';
import { Toaster } from 'sonner';
import ChatNotificationWidget from './components/ChatNotificationWidget';
import UpdateNotification from './components/UpdateNotification';
import './App.css';

// Component to handle initial route restoration
function InitialRouteHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    navigate('/tasks', { replace: true });
  }, [navigate]);

  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        {user && <ChatNotificationWidget />}
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/tasks" replace />} />
          <Route
            path="/"
            element={
              user ? <Layout user={user} /> : <Navigate to="/login" replace />
            }
          >
            <Route index element={<InitialRouteHandler />} />
            <Route path="overview" element={<Overview />} />
            <Route path="tasks" element={<Dashboard />} />
            <Route path="team-chat" element={<TeamChat />} />
            <Route path="logger" element={<Logger />} />
            {/* <Route path="inventory" element={<Inventory />} /> */}
            <Route path="personal-notes" element={<PersonalNotes />} />
            <Route path="experiment-tracking" element={<ExperimentProjects />} />
            <Route path="experiment-tracking/:projectId" element={<ExperimentTracking />} />
            <Route path="project-detail/:projectId" element={<ProjectDetail />} />
            <Route path="lab-store" element={<LabStore />} />
            <Route path="lab-store/3d-printer" element={<PrinterUsage />} />
            <Route path="lab-store/inventory" element={<LabInventory />} />
            <Route path="ai-assistant" element={<AIAssistant />} />
            <Route path="documents" element={<Documents />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="settings" element={<Settings />} />
            <Route path="profile" element={<Profile />} />
            <Route path="admin/users" element={<UserManagement />} />
          </Route>
          <Route path="*" element={<Navigate to={user ? "/tasks" : "/login"} replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" expand={false} richColors={false} />
      <UpdateNotification />
    </div>
  );
}

export default App;
